import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import { 
  Mail, ArrowLeft, CheckCircle, Eye, Loader2, AlertCircle
} from 'lucide-react';
import { FadeIn, AnimatedCard } from '../components/ui/AnimatedCard';
import toast from 'react-hot-toast';

export function AdminContacts() {
  const queryClient = useQueryClient();
  const [selectedContact, setSelectedContact] = useState<any>(null);

  const { data: contacts, isLoading } = useQuery({
    queryKey: ['admin-contacts'],
    queryFn: async () => {
      const res = await adminApi.getContacts();
      return res.data;
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => 
      adminApi.updateContactStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-contacts'] });
      toast.success('Status updated');
    },
    onError: () => toast.error('Failed to update status'),
  });

  const allContacts = contacts || [];

  const statusColors: Record<string, string> = {
    new: 'bg-red-100 text-red-700',
    read: 'bg-blue-100 text-blue-700',
    replied: 'bg-green-100 text-green-700',
  };

  return (
    <div className="space-y-6">
      <FadeIn>
        <div className="flex items-center gap-4">
          <Link to="/admin" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Contact Submissions</h1>
            <p className="text-slate-500">Manage contact form submissions</p>
          </div>
        </div>
      </FadeIn>

      <FadeIn>
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'New', count: allContacts.filter((c: any) => c.status === 'new').length, color: 'bg-red-50 text-red-700' },
            { label: 'Read', count: allContacts.filter((c: any) => c.status === 'read').length, color: 'bg-blue-50 text-blue-700' },
            { label: 'Replied', count: allContacts.filter((c: any) => c.status === 'replied').length, color: 'bg-green-50 text-green-700' },
          ].map((stat) => (
            <AnimatedCard key={stat.label} className="p-4 text-center">
              <div className={`text-2xl font-bold ${stat.color.split(' ')[1]}`}>{stat.count}</div>
              <div className="text-sm text-slate-500">{stat.label}</div>
            </AnimatedCard>
          ))}
        </div>
      </FadeIn>

      <FadeIn>
        <AnimatedCard className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : (
            <div className="space-y-3">
              {allContacts.map((contact: any) => (
                <div
                  key={contact.id}
                  className={`p-4 rounded-xl border-2 transition-colors cursor-pointer ${
                    selectedContact?.id === contact.id 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-transparent bg-slate-50 hover:bg-slate-100'
                  }`}
                  onClick={() => setSelectedContact(selectedContact?.id === contact.id ? null : contact)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold">
                        {contact.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{contact.name}</div>
                        <div className="text-sm text-slate-500">{contact.email}</div>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${statusColors[contact.status] || 'bg-slate-100 text-slate-700'}`}>
                      {contact.status}
                    </span>
                  </div>

                  <div className="text-sm font-medium text-slate-700 mb-1">{contact.subject}</div>
                  <div className="text-sm text-slate-600 line-clamp-2">{contact.message}</div>

                  {selectedContact?.id === contact.id && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="text-sm text-slate-600 mb-3">{contact.message}</div>
                      <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
                        {contact.phone && <span>Phone: {contact.phone}</span>}
                        <span>Submitted: {contact.createdAt ? new Date(contact.createdAt).toLocaleString() : '-'}</span>
                      </div>
                      <div className="flex gap-2">
                        {contact.status === 'new' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateStatusMutation.mutate({ id: contact.id, status: 'read' });
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Eye className="w-3 h-3" />
                            Mark as Read
                          </button>
                        )}
                        {contact.status !== 'replied' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateStatusMutation.mutate({ id: contact.id, status: 'replied' });
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Mark as Replied
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {allContacts.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No contact submissions yet</p>
                </div>
              )}
            </div>
          )}
        </AnimatedCard>
      </FadeIn>
    </div>
  );
}
